(() => {
  'use strict';

  const root = document.querySelector('[data-trm-air]');
  if (!root) return;

  const GARAGE_KEY = 'trmSelectedVehicleV1';
  const catalog = window.TRM_AIR_SPRING_INDEX_V21 || { kits: [], controls: [] };
  const kits = Array.isArray(catalog.kits) ? catalog.kits : [];
  const controls = Array.isArray(catalog.controls) ? catalog.controls : [];
  const variantCatalog = window.TRM_AIR_VARIANTS_V22?.products || {};
  const form = root.querySelector('[data-trm-air-form]');
  const results = root.querySelector('[data-trm-air-results]');
  const progress = [...root.querySelectorAll('[data-trm-step-jump]')];
  let currentStep = 1;
  let maxStep = 1;
  let educationOnly = false;

  const escapeHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  const normalize = (value) => String(value || '')
    .toLowerCase()
    .replace(/chevrolet/g, 'chevy')
    .replace(/f[-\s]?(\d{3})/g, 'f $1')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  const readVehicle = () => {
    try { return JSON.parse(window.localStorage.getItem(GARAGE_KEY) || 'null'); }
    catch (error) { return null; }
  };

  const exactYearFrom = (vehicle) => {
    const candidate = String(vehicle?.exactYear || vehicle?.exact_year || vehicle?.modelYear || vehicle?.model_year || vehicle?.year || '');
    return /^(?:19|20)\d{2}$/.test(candidate) ? candidate : '';
  };

  const drivetrainFrom = (vehicle) => {
    const raw = normalize(vehicle?.drivetrain || vehicle?.drive || vehicle?.fitmentDrive || vehicle?.fitment_drive || '');
    if (/\b(?:4wd|awd|4x4|four wheel drive)\b/.test(raw)) return '4wd';
    if (/\b(?:2wd|rwd|4x2|two wheel drive)\b/.test(raw)) return '2wd';
    return raw === 'unknown' ? 'unknown' : '';
  };

  const saveVehicleDetail = (detail) => {
    const vehicle = readVehicle();
    if (!vehicle) return;
    try {
      window.localStorage.setItem(GARAGE_KEY, JSON.stringify({ ...vehicle, ...detail }));
      window.dispatchEvent(new CustomEvent('trm:vehicle-detail-changed', { detail }));
    } catch (error) {}
  };

  const yearsForVehicle = (vehicle) => {
    const raw = String(`${vehicle?.key || ''} ${vehicle?.label || ''}`).toLowerCase();
    const range = raw.match(/\b((?:19|20)\d{2})\s*(?:-|–|to)\s*((?:19|20)\d{2})\b/);
    if (range) {
      const min = Number(range[1]);
      const max = Math.min(Number(range[2]), new Date().getFullYear() + 1);
      return Array.from({ length: Math.max(0, max - min + 1) }, (_, index) => min + index);
    }
    const plus = raw.match(/\b((?:19|20)\d{2})(?:\s*\+(?=\s|$)|[-\s]+plus\b)/);
    if (plus) {
      const min = Number(plus[1]);
      const max = new Date().getFullYear() + 1;
      return Array.from({ length: Math.max(0, max - min + 1) }, (_, index) => min + index);
    }
    const single = raw.match(/\b((?:19|20)\d{2})\b/);
    return single ? [Number(single[1])] : [];
  };

  const vehicleCollectionUrl = (vehicle) => vehicle?.key
    ? `/collections/${String(vehicle.key).replace(/^vehicle:/, '')}-suspension`
    : '/collections/truck-suspension-lift-kits';

  const productHandle = (product) => String(product?.url || '').split('/products/')[1]?.split(/[?#/]/)[0] || '';

  const variantFor = (product) => variantCatalog[productHandle(product)] || null;

  const makeFamily = (value) => {
    const raw = normalize(value);
    if (/\bford\b/.test(raw)) return 'ford';
    if (/\b(?:chevy|gmc|gm)\b/.test(raw)) return 'gm';
    if (/\b(?:ram|dodge)\b/.test(raw)) return 'ram';
    if (/\btoyota\b/.test(raw)) return 'toyota';
    if (/\bnissan\b/.test(raw)) return 'nissan';
    if (/\bjeep\b/.test(raw)) return 'jeep';
    if (/\bhonda\b/.test(raw)) return 'honda';
    return '';
  };

  const modelFamily = (value) => {
    const raw = normalize(value);
    const families = [
      ['f150', /\bf\s*150\b/], ['f250', /\bf\s*250\b/], ['f350', /\bf\s*350\b/],
      ['f450', /\bf\s*450\b/], ['f550', /\bf\s*550\b/],
      ['silverado1500', /\bsilverado\s*1500\b/], ['silverado2500', /\bsilverado\s*2500\b/],
      ['silverado3500', /\bsilverado\s*3500\b/], ['sierra1500', /\bsierra\s*1500\b/],
      ['sierra2500', /\bsierra\s*2500\b/], ['sierra3500', /\bsierra\s*3500\b/],
      ['ram1500', /\b(?:ram|dodge ram)\s*1500\b/], ['ram2500', /\b(?:ram|dodge ram)\s*2500\b/],
      ['ram3500', /\b(?:ram|dodge ram)\s*3500\b/], ['tundra', /\btundra\b/],
      ['tacoma', /\btacoma\b/], ['4runner', /\b4\s*runner\b/], ['sequoia', /\bsequoia\b/],
      ['ranger', /\branger\b/], ['bronco', /\bbronco\b/], ['maverick', /\bmaverick\b/],
      ['colorado', /\bcolorado\b/], ['canyon', /\bcanyon\b/], ['tahoe', /\btahoe\b/],
      ['suburban', /\bsuburban\b/], ['yukon', /\byukon\b/], ['avalanche', /\bavalanche\b/],
      ['frontier', /\bfrontier\b/], ['titan', /\btitan\b/], ['armada', /\barmada\b/],
      ['wrangler', /\bwrangler\b/], ['gladiator', /\bgladiator\b/], ['grandcherokee', /\bgrand\s*cherokee\b/],
      ['ridgeline', /\bridgeline\b/], ['pilot', /\bpilot\b/], ['odyssey', /\bodyssey\b/],
      ['transit', /\btransit\b/], ['sprinter', /\bsprinter\b/], ['express', /\bexpress\b/],
      ['savana', /\bsavana\b/], ['econoline', /\beconoline\b/], ['excursion', /\bexcursion\b/]
    ];
    return families.find(([, pattern]) => pattern.test(raw))?.[0] || '';
  };

  const vehicleProfile = (vehicle, fields = {}) => {
    if (!vehicle) return null;
    const raw = normalize(`${vehicle.key || ''} ${vehicle.label || ''}`);
    const year = Number(fields.exact_year || raw.match(/\b(?:19|20)\d{2}\b/)?.[0] || 0);
    return { raw, year, make: makeFamily(raw), family: modelFamily(raw), label: vehicle.label || raw };
  };

  const yearWindows = (value) => {
    const raw = String(value || '').toLowerCase();
    const expand = (value, reference = 0) => {
      const year = Number(value);
      if (String(value).length === 4) return year;
      if (reference >= 2000) return 2000 + year;
      return year >= 80 ? 1900 + year : 2000 + year;
    };
    const ranges = [...raw.matchAll(/\b(\d{2}|\d{4})\s*[-–]\s*(\d{2}|\d{4})\b/g)]
      .map((match) => {
        const min = expand(match[1]);
        return { min, max: expand(match[2], min) };
      })
      .filter((range) => range.min >= 1980 && range.max <= 2099 && range.min <= range.max);
    if (ranges.length) return ranges;
    const years = [...raw.matchAll(/\b((?:19|20)\d{2})\b/g)].map((match) => Number(match[1]));
    return [...new Set(years)].map((year) => ({ min: year, max: year }));
  };

  const specialTerms = [
    ['raptor', /\braptor\b/], ['powerboost', /\bpowerboost\b/], ['zr2', /\bzr2\b/],
    ['at4x', /\bat4x\b/], ['power wagon', /\bpower\s*wagon\b/],
    ['trx', /\btrx\b/], ['tremor', /\btremor\b/]
  ];

  const exclusionForProfile = (product, profile, fields) => {
    const notes = normalize((product.notes || []).join(' '));
    const application = normalize(`${product.title || ''} ${notes}`);
    const has2wd = /\b(?:2wd|rwd|two wheel drive)\b/.test(application);
    const has4wd = /\b(?:4wd|awd|four wheel drive)\b/.test(application);
    if (fields?.drivetrain === '2wd' && has4wd && !has2wd) return 'Catalog application is limited to 4WD/AWD.';
    if (fields?.drivetrain === '4wd' && has2wd && !has4wd) return 'Catalog application is limited to 2WD/RWD.';
    if (!notes) return '';
    for (const [name, pattern] of specialTerms) {
      if (profile?.raw && pattern.test(profile.raw)) {
        const exclusion = new RegExp(`(?:does not fit|do not fit|will not fit|not compatible)[a-z0-9 ]{0,90}${name.replace(' ', '\\s*')}`);
        if (exclusion.test(notes)) return `Catalog notes exclude ${name}.`;
      }
    }
    if (fields?.factory_air === 'yes' && /(?:does not fit|not compatible)[a-z0-9 ]{0,90}factory[a-z0-9 ]{0,30}air/.test(notes)) return 'Catalog notes exclude this factory air-suspension configuration.';
    if (fields?.hitch === 'yes' && /(?:does not fit|not compatible)[a-z0-9 ]{0,90}(?:fifth|5th|gooseneck|in bed hitch)/.test(notes)) return 'Catalog notes exclude this in-bed hitch configuration.';
    return '';
  };

  const vehicleEvidence = (product, profile, fields) => {
    if (!profile) return { match: false, score: 0, evidence: '', excluded: '' };
    const productTitle = normalize(product.title);
    const profileSpecial = specialTerms.find(([, pattern]) => pattern.test(profile.raw));
    const productSpecial = specialTerms.find(([, pattern]) => pattern.test(productTitle));
    if (fields?.special_trim === 'yes' && !profileSpecial) return { match: false, score: 0, evidence: '', excluded: 'Exact special trim is required.' };
    if (productSpecial && !profileSpecial) return { match: false, score: 0, evidence: '', excluded: 'Special-model-only product.' };
    if (profileSpecial && productSpecial && productSpecial[0] !== profileSpecial[0]) return { match: false, score: 0, evidence: '', excluded: 'Different special model.' };
    const excluded = exclusionForProfile(product, profile, fields);
    if (excluded) return { match: false, score: 0, evidence: '', excluded };
    const fitRecords = product.fits || [];
    const candidates = (fitRecords.length ? fitRecords : [product.title]).map((item) => String(item).replace(/^vehicle:/i, ''));
    const matching = candidates.filter((item) => {
      const make = makeFamily(item);
      const family = modelFamily(item);
      const windows = yearWindows(item);
      if (profile.make && make && make !== profile.make) return false;
      if (profile.family && family && family !== profile.family) return false;
      if (profile.year && windows.length && !windows.some((range) => profile.year >= range.min && profile.year <= range.max)) return false;
      return Boolean((profile.family && family === profile.family) || (!profile.family && profile.make && make === profile.make));
    });
    if (!matching.length) return { match: false, score: 0, evidence: '', excluded: '' };
    const best = matching.find((item) => yearWindows(item).length && modelFamily(item) === profile.family) || matching[0];
    const exactYear = yearWindows(best).some((range) => profile.year >= range.min && profile.year <= range.max);
    return { match: true, score: 70 + (modelFamily(best) === profile.family ? 18 : 0) + (exactYear ? 12 : 0), evidence: exactYear ? 'Vehicle + model year evidence' : 'Vehicle-family evidence', excluded: '' };
  };

  const familyMeta = {
    'air-lift-1000': { label:'Air Lift 1000', title:'Air Lift 1000 coil-spring support', capacity:'Up to 1,000 lb of load-leveling capacity', copy:'A compact polyurethane insert for compatible rear-coil applications and lighter variable loads.', wins:['Low-cost application-specific support','Adjustable when loaded and unloaded'], watch:'Less leveling headroom than a frame-to-axle LoadLifter bellows.' },
    'air-lift-1000-hd': { label:'Air Lift 1000 HD', title:'Air Lift 1000 HD coil-spring support', capacity:'Up to 2,100 lb of load-leveling capacity', copy:'A heavier-duty polyurethane insert for supported rear-coil applications.', wins:['More support than standard Air Lift 1000','Fits inside the listed coil spring'], watch:'Only use on an exact 1000 HD application; it is not a universal LoadLifter substitute.' },
    ridecontrol: { label:'RideControl', title:'Air Lift RideControl helper spring', capacity:'Up to 2,000 lb on supported applications', copy:'An adjustable sleeve-style helper spring for lighter truck, van and SUV applications.', wins:['Good value for moderate variable loads','Application-specific brackets'], watch:'Not the same construction or capacity class as LoadLifter 5000.' },
    slamair: { label:'SlamAir', title:'Air Lift SlamAir load support', capacity:'Application-specific lowered-truck support', copy:'A specialized load-leveling path for supported lowered trucks.', wins:['Designed around selected lowered applications','Adjustable support for load'], watch:'Ride height and exact lowered-suspension architecture must be verified.' },
    'loadlifter-5000': { label:'LoadLifter 5000', title:'LoadLifter 5000', capacity:'Up to 5,000 lb of load-leveling capacity', copy:'The broad-coverage workhorse for many leaf-spring trucks.', wins:['Excellent price-to-capability balance','Broad 1/2-, 3/4- and 1-ton application range'], watch:'The standard kit does not include the Ultimate internal jounce bumper.' },
    'loadlifter-5000-ultimate': { label:'5000 Ultimate', title:'LoadLifter 5000 Ultimate', capacity:'Up to 5,000 lb with internal jounce support', copy:'The internal jounce bumper adds progressive backup support.', wins:['Three-stage internal jounce bumper','Better unloaded and emergency-support envelope'], watch:'It costs more than standard 5000 but does not increase the truck rating or 5,000-lb class.' },
    'loadlifter-7500': { label:'7500 XL Ultimate', title:'LoadLifter 7500 XL Ultimate', capacity:'Up to 7,500 lb of load-leveling capacity', copy:'A larger seven-inch bellows for severe load on supported heavy-duty trucks.', wins:['More support at lower pressure','Internal jounce bumper and severe-duty headroom'], watch:'Narrower application coverage; unused capacity is not better fitment.' }
  };

  const jobLabels = { cargo:'bed cargo & tools', travel:'travel-trailer tongue load', fifth:'5th-wheel / gooseneck pin load', camper:'slide-in camper', commercial:'commercial / constant load' };
  const loadLabels = { '500':'under 750 lb on truck', '1250':'750–1,500 lb on truck', '2250':'1,500–2,500 lb on truck', '3250':'2,500–3,500 lb on truck', '4250':'3,500+ lb on truck', unknown:'truck-carried load not measured' };
  const controlMeta = {
    manual:{ title:'Manual fill valves', label:'Manual control', copy:'Use the included fill valves with an external compressor and accurate gauge.', wins:['Lowest entry cost','Simple, serviceable plumbing'], watch:'You must stop, check and fill manually when the load changes.' },
    'analog-single':{ title:'LoadController single path', label:'In-cab control', copy:'A physical gauge and switch inflate both air springs equally.', wins:['Always-available physical control','No phone required'], watch:'Single path cannot correct an uneven left/right load.' },
    'wireless-single':{ title:'WirelessOne', label:'Single-path wireless', copy:'Remote and app control both air springs together with three presets.', wins:['Simple wireless operation','Good for balanced towing loads'], watch:'WirelessOne is not currently stocked in the audited TRM catalog.' },
    'wireless-dual':{ title:'WirelessAir 2nd Gen', label:'Dual-path wireless', copy:'Remote and app control left and right independently with three presets.', wins:['Corrects side-to-side imbalance','No air lines enter the cab'], watch:'Higher cost than manual or single-path control.' },
    tank:{ title:'WirelessAir + 2-gallon tank', label:'Dual path + stored air', copy:'The tank adds quicker inflation and a hose for tires or gear.', wins:['Fast pressure changes','Accessory air from the coiled hose'], watch:'Not currently stocked and adds no load-leveling capacity.' }
  };

  const isHeavyDutyTruck = (profile) => new Set(['f250','f350','f450','f550','silverado2500','silverado3500','sierra2500','sierra3500','ram2500','ram3500']).has(profile?.family);

  const preferredFamilies = (fields, profile) => {
    const load = Number(fields.truck_load || 0);
    const hardDuty = fields.frequency === 'daily' || fields.frequency === 'frequent' || fields.job === 'commercial' || fields.job === 'camper' || fields.job === 'fifth';
    if (load >= 3500 && isHeavyDutyTruck(profile)) return ['loadlifter-7500','loadlifter-5000-ultimate','loadlifter-5000'];
    if ((load >= 2500 || (hardDuty && load >= 1500)) && isHeavyDutyTruck(profile)) return ['loadlifter-7500','loadlifter-5000-ultimate','loadlifter-5000'];
    if (load >= 1500 || hardDuty || fields.truck_load === 'unknown') return ['loadlifter-5000-ultimate','loadlifter-5000','loadlifter-7500','air-lift-1000-hd'];
    if (load >= 750) return ['air-lift-1000-hd','loadlifter-5000','loadlifter-5000-ultimate','ridecontrol','air-lift-1000'];
    return ['air-lift-1000','air-lift-1000-hd','ridecontrol','loadlifter-5000','loadlifter-5000-ultimate'];
  };

  const activeFields = () => Object.fromEntries(new FormData(form).entries());

  const getVehicleMatches = (fields) => {
    const profile = vehicleProfile(readVehicle(), fields);
    if (!profile || educationOnly) return [];
    return kits.map((product) => {
      const vehicle = vehicleEvidence(product, profile, fields);
      if (!vehicle.match) return null;
      return { product, vehicle, score: vehicle.score + Math.min(12, Math.log10(Math.max(1, product.inventory)) * 4) };
    }).filter(Boolean);
  };

  const chooseFamily = (fields, profile, vehicleMatches) => {
    const preferences = preferredFamilies(fields, profile);
    const available = new Set(vehicleMatches.map((match) => match.product.family));
    const family = preferences.find((name) => available.has(name)) || vehicleMatches[0]?.product.family || preferences[0];
    return { family, desired: preferences[0], adjusted: family !== preferences[0], preferences };
  };

  const showStep = (step, scroll = true) => {
    const target = Math.max(1, Math.min(5, Number(step) || 1));
    currentStep = target;
    maxStep = Math.max(maxStep, target);
    root.querySelectorAll('[data-trm-air-step]').forEach((panel) => {
      const active = Number(panel.dataset.trmAirStep) === target;
      panel.hidden = !active;
      panel.querySelectorAll('[data-trm-back],[data-trm-next]').forEach((control) => {
        const waitingForExactYear = control.matches('[data-trm-air-use-truck]') && Boolean(readVehicle()) && !root.querySelector('[data-trm-air-exact-year]')?.value;
        control.disabled = !active || waitingForExactYear;
      });
    });
    progress.forEach((button) => {
      const number = Number(button.dataset.trmStepJump);
      button.disabled = number > maxStep;
      button.classList.toggle('is-complete', number < target);
      if (number === target) button.setAttribute('aria-current', 'step');
      else button.removeAttribute('aria-current');
    });
    if (scroll) root.querySelector(`[data-trm-air-step="${target}"]`)?.scrollIntoView({ behavior:'smooth', block:'start' });
  };

  const openGarage = (trigger) => window.dispatchEvent(new CustomEvent('trm:garage-open', { detail:{ trigger, source:'air-spring-desk-v21' } }));

  let vehicleImageRequest = 0;

  const syncVehicleImage = (vehicle) => {
    const image = root.querySelector('[data-trm-air-vehicle-image]');
    if (!image) return;
    const fallback = 'https://cdn.shopify.com/s/files/1/0755/5933/8020/files/trm-air-spring-guide-hero-air-lift.jpg?v=1788270317';
    image.src = fallback;
    image.alt = vehicle?.label ? `${vehicle.label} selected for the air spring guide` : 'Pickup truck selected for the air spring guide';
    const sourceUrl = vehicle?.sourceUrl || vehicleCollectionUrl(vehicle);
    if (!sourceUrl || !sourceUrl.startsWith('/collections/')) return;
    const request = ++vehicleImageRequest;
    fetch(sourceUrl, { credentials:'same-origin' })
      .then((response) => response.ok ? response.text() : '')
      .then((html) => {
        if (!html || request !== vehicleImageRequest) return;
        const documentCopy = new DOMParser().parseFromString(html, 'text/html');
        const source = documentCopy.querySelector('meta[property="og:image:secure_url"],meta[property="og:image"]')?.content;
        if (source) image.src = source;
      })
      .catch(() => {});
  };

  const syncVehicle = () => {
    const vehicle = readVehicle();
    const label = vehicle?.label || 'Choose your truck for fitment-ranked results';
    root.querySelectorAll('[data-trm-air-vehicle]').forEach((node) => { node.textContent = label; });
    const stage = root.querySelector('[data-trm-air-stage-vehicle]');
    const stageCopy = root.querySelector('[data-trm-air-stage-copy]');
    const stageStatus = root.querySelector('[data-trm-air-stage-status]');
    const yearField = root.querySelector('[data-trm-air-year-field]');
    const useTruck = root.querySelector('[data-trm-air-use-truck]');
    if (stage) stage.textContent = vehicle?.label || 'No truck selected yet';
    if (stageCopy) stageCopy.textContent = vehicle ? 'Loaded from My Truck Garage. We ask only for vehicle details that are not saved yet.' : 'Choose a truck for fitment-ranked products, or use the guide in information mode.';
    root.querySelectorAll('[data-trm-air-garage-open]').forEach((button) => { button.textContent = vehicle ? 'Change truck' : 'Choose truck'; });
    const exactYear = root.querySelector('[data-trm-air-exact-year]');
    if (exactYear) {
      const previous = exactYear.value;
      const years = yearsForVehicle(vehicle);
      const saved = exactYearFrom(vehicle);
      exactYear.replaceChildren(new Option(years.length ? 'Choose exact model year' : 'Choose your truck first', ''));
      years.forEach((year) => exactYear.add(new Option(String(year), String(year))));
      exactYear.disabled = !years.length;
      if (years.map(String).includes(saved)) exactYear.value = saved;
      else if (years.map(String).includes(previous)) exactYear.value = previous;
      else if (years.length === 1) exactYear.value = String(years[0]);
      const ready = Boolean(vehicle && exactYear.value);
      if (yearField) yearField.hidden = Boolean(ready && saved);
      if (stage) stage.textContent = ready ? `${exactYear.value} ${vehicle?.label?.replace(/^\s*(?:19|20)\d{2}\s*(?:-|–|to)\s*(?:19|20)\d{2}\s*/i, '') || vehicle?.label || ''}`.trim() : (vehicle?.label || 'No truck selected yet');
      if (stageStatus) {
        stageStatus.classList.remove('is-error');
        stageStatus.textContent = !vehicle
          ? 'No fitment matching is active yet.'
          : ready
            ? 'Truck and exact model year are ready. Continue—nothing needs to be entered again.'
            : 'Garage knows the truck generation; choose only the missing exact model year.';
      }
      if (useTruck) {
        useTruck.disabled = Boolean(vehicle && !ready);
        useTruck.innerHTML = `${!vehicle ? 'Choose my truck' : ready ? 'Continue with this truck' : 'Choose the missing year above'} <span>→</span>`;
      }
    }
    const drivetrain = root.querySelector('[name="drivetrain"]');
    const savedDrive = drivetrainFrom(vehicle);
    if (drivetrain && savedDrive && [...drivetrain.options].some((option) => option.value === savedDrive)) drivetrain.value = savedDrive;
    const browse = root.querySelector('[data-trm-air-browse]');
    if (browse) browse.href = vehicleCollectionUrl(vehicle);
    syncVehicleImage(vehicle);
  };

  const validateStep = (step) => {
    const panel = root.querySelector(`[data-trm-air-step="${step}"]`);
    const requiredGroups = [...new Set([...panel.querySelectorAll('[required]')].map((field) => field.name))];
    for (const name of requiredGroups) {
      const fields = [...panel.querySelectorAll(`[name="${CSS.escape(name)}"]`)];
      const valid = fields.some((field) => (field.type === 'radio' || field.type === 'checkbox') ? field.checked : field.value);
      if (!valid) {
        const first = fields[0];
        const status = step === 1 ? root.querySelector('[data-trm-air-stage-status]') : null;
        if (status) {
          status.classList.add('is-error');
          status.textContent = readVehicle() ? 'Choose the one missing model year before continuing.' : 'Choose a truck first, or use information mode.';
        }
        first?.focus();
        first?.closest('label')?.scrollIntoView({ behavior:'smooth', block:'center' });
        return false;
      }
    }
    return true;
  };

  const effectiveControl = (fields) => fields.uneven === 'yes' && !['wireless-dual','tank'].includes(fields.control) ? 'wireless-dual' : (fields.control || 'manual');

  const familyWhy = (fields, adjusted) => {
    const pieces = [];
    pieces.push(fields.truck_load === 'unknown' ? 'The truck-carried load still needs measurement.' : `${loadLabels[fields.truck_load]} puts this family in the useful support range.`);
    if (fields.frequency === 'daily' || fields.frequency === 'frequent') pieces.push('Frequent pressure cycles favor durable construction and convenient onboard air.');
    if (fields.job === 'camper' || fields.uneven === 'yes') pieces.push('The high or uneven load raises the value of independent control.');
    if (adjusted) pieces.push('The use-case preference was adjusted to the family with stronger current vehicle evidence.');
    return pieces.join(' ');
  };

  const recommendationHtml = (meta, label, why) => `
    <span>${escapeHtml(label)}</span>
    <h3>${escapeHtml(meta.title)}</h3>
    <p>${escapeHtml(meta.capacity || meta.copy)}</p>
    <ul>${(meta.wins || []).map((item) => `<li>${escapeHtml(item)}</li>`).join('')}</ul>
    <p><b>Why here:</b> ${escapeHtml(why)}</p>
    <p><b>Know before buying:</b> ${escapeHtml(meta.watch)}</p>`;

  const familyLabel = (family) => familyMeta[family]?.label || 'Application-specific Air Lift kit';

  const productWhy = (match, selectedFamily) => {
    if (match.product.family === selectedFamily) return 'Vehicle/model-year evidence and the recommended spring family both align.';
    if (match.product.family === 'loadlifter-5000-ultimate') return 'Vehicle evidence aligns; Ultimate adds an internal jounce bumper as a premium alternative.';
    if (match.product.family === 'loadlifter-5000') return 'Vehicle evidence aligns; standard 5000 is the value alternative.';
    if (match.product.family === 'loadlifter-7500') return 'Vehicle evidence aligns; verify that the truck-carried load justifies the XL system.';
    return 'Vehicle/model-year catalog evidence aligns; compare construction and capacity with the main recommendation.';
  };

  const relevantNote = (product) => {
    const note = (product.notes || []).find((item) => /does not fit|not compatible|factory air|fifth wheel|5th wheel|gooseneck|drw|srw|cab chassis|lifted/i.test(item)) || '';
    if (!note) return '';
    const patterns = [
      /\b(?:does not fit|do not fit|will not fit|not compatible with)\b.{0,155}/i,
      /\b(?:fits vehicles with|compatible with)\b.{0,145}/i
    ];
    const condition = patterns.map((pattern) => note.match(pattern)?.[0]).find(Boolean);
    if (!condition) return '';
    return condition
      .split(/\b(?:Maintenance|Air Adjustable|Installation Instructions|Limited Lifetime|Warranty)\b/i)[0]
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/[;,:\s]+$/, '')
      .slice(0, 180);
  };

  const directCartAllowed = (product, fields) => {
    const variant = variantFor(product);
    if (!variant?.available || !variant.variantId) return false;
    if (!fields.exact_year || !fields.drivetrain || fields.drivetrain === 'unknown') return false;
    if (fields.rear_lift !== '0' || fields.hitch === 'yes' || fields.factory_air === 'yes' || fields.special_trim === 'yes') return false;
    return !relevantNote(product);
  };

  const renderProductCard = (match, selectedFamily, fields, rank) => {
    const product = match.product;
    const price = new Intl.NumberFormat('en-US', { style:'currency', currency:product.currency || 'USD', maximumFractionDigits:2 }).format(product.price);
    const note = relevantNote(product);
    const badges = [familyLabel(product.family), match.vehicle.evidence, product.capacity ? `${product.capacity.toLocaleString()}-lb class` : 'Application-specific'];
    const directAdd = directCartAllowed(product, fields);
    const handle = productHandle(product);
    return `
      <article class="trm-air-card${rank === 0 ? ' is-best-match' : ''}">
        <div class="trm-air-card__media">
          ${product.image && !/(warning|prop[-_ ]?65|cancer|reproduction|logo|brand[-_ ]?mark)/i.test(product.image) ? `<img src="${escapeHtml(product.image)}" alt="${escapeHtml(product.title)}" width="640" height="480" loading="lazy">` : '<b class="trm-air-card__image-fallback" aria-hidden="true">AIR LIFT</b>'}
          <span class="trm-air-card__family">${escapeHtml(familyLabel(product.family))}</span>
          <span class="trm-air-card__stock">In stock: ${escapeHtml(product.inventory)}</span>
        </div>
        <div class="trm-air-card__body">
          <span class="trm-air-card__sku">${escapeHtml(product.sku || 'Air Lift')}</span>
          <h3>${escapeHtml(product.title)}</h3>
          <div class="trm-air-card__badges">${badges.map((badge) => `<span>${escapeHtml(badge)}</span>`).join('')}</div>
          <p class="trm-air-card__why">${escapeHtml(productWhy(match, selectedFamily))}</p>
          ${note ? `<p class="trm-air-card__note"><b>Catalog condition:</b> ${escapeHtml(note)}</p>` : ''}
          <div class="trm-air-card__footer"><span class="trm-air-card__price">${escapeHtml(price)}<small>Current listing price</small></span><div class="trm-air-card__actions"><a href="${escapeHtml(product.url)}">Review fitment →</a><button class="trm-air__cart-button" type="button" data-trm-air-add="${escapeHtml(handle)}" ${directAdd ? '' : 'disabled'}>${directAdd ? 'Add spring kit' : 'Verify on product page'}</button></div></div>
        </div>
      </article>`;
  };

  const controlProductCard = (product, reason) => {
    const price = new Intl.NumberFormat('en-US', { style:'currency', currency:product.currency || 'USD', maximumFractionDigits:2 }).format(product.price);
    const handle = productHandle(product);
    const variant = variantFor(product);
    return `<article class="trm-air-control-card">${product.image ? `<img src="${escapeHtml(product.image)}" alt="${escapeHtml(product.title)}" width="240" height="240" loading="lazy">` : ''}<div><small>${escapeHtml(product.sku)} · IN STOCK ${escapeHtml(product.inventory)}</small><h3>${escapeHtml(product.title)}</h3><p>${escapeHtml(reason)} · ${escapeHtml(price)}</p><div class="trm-air-control-card__actions"><a href="${escapeHtml(product.url)}">View system →</a><button class="trm-air__cart-button" type="button" data-trm-air-add="${escapeHtml(handle)}" ${variant?.available ? '' : 'disabled'}>Add air management</button></div></div></article>`;
  };

  const requestCard = (type) => {
    const meta = controlMeta[type];
    const image = type === 'tank' ? 'https://cdn.shopify.com/s/files/1/0755/5933/8020/files/trm-wirelessair-tank-74100ez.png?v=1788270338' : 'https://cdn.shopify.com/s/files/1/0755/5933/8020/files/trm-wirelessair-system.png?v=1788270309';
    return `<article class="trm-air-control-card is-request"><img src="${image}" alt="${escapeHtml(meta.title)}" width="240" height="240" loading="lazy"><div><small>NOT CURRENTLY STOCKED</small><h3>${escapeHtml(meta.title)}</h3><p>${escapeHtml(meta.copy)}</p><a href="#trm-air-finder" data-trm-air-tex>Ask Tex to source it →</a></div></article>`;
  };

  const manualCard = () => `<article class="trm-air-control-card is-request"><img src="https://cdn.shopify.com/s/files/1/0755/5933/8020/files/trm-loadlifter-5000-kit.png?v=1788270350" alt="Manual air spring fill valves" width="240" height="240" loading="lazy"><div><small>INCLUDED WITH SPRING KIT</small><h3>Manual inflation</h3><p>Use the included Schrader valves with an external compressor and accurate gauge.</p><a href="#trm-air-5000-vs-7500">Compare spring families →</a></div></article>`;

  const controlProductsFor = (type, fields) => {
    if (type === 'analog-single') {
      const skus = fields.uneven === 'yes' ? ['ALF25856','ALF25854'] : ['ALF25854'];
      return controls.filter((product) => skus.includes(product.sku)).sort((a,b) => skus.indexOf(a.sku)-skus.indexOf(b.sku));
    }
    if (type === 'wireless-dual') return controls.filter((product) => ['ALF74000EZ','ALF74000'].includes(product.sku)).sort((a,b) => Number(b.ez)-Number(a.ez));
    if (type === 'tank') return controls.filter((product) => product.sku === 'ALF74000EZ');
    if (type === 'wireless-single') return controls.filter((product) => product.sku === 'ALF25854');
    return [];
  };

  const controlResults = (type, fields) => {
    const cards = [];
    if (type === 'manual') cards.push(manualCard());
    if (type === 'analog-single') {
      controlProductsFor(type, fields).forEach((p) => cards.push(controlProductCard(p, p.sku === 'ALF25856' ? 'Dual path for independent left/right adjustment' : 'Single path, in-cab gauge and heavy-duty compressor')));
    }
    if (type === 'wireless-single') {
      cards.push(requestCard('wireless-single'));
      controlProductsFor(type, fields).forEach((p) => cards.push(controlProductCard(p, 'In-stock single-path alternative with physical gauge')));
    }
    if (type === 'wireless-dual') controlProductsFor(type, fields).forEach((p) => cards.push(controlProductCard(p, p.ez ? 'Dual path, remote + app, preassembled EZ Mount' : 'Dual path, remote + app, standard mounting')));
    if (type === 'tank') {
      cards.push(requestCard('tank'));
      controlProductsFor(type, fields).forEach((p) => cards.push(controlProductCard(p, 'In-stock wireless base if accessory tank air is not required')));
    }
    return cards.join('');
  };

  const buildAlerts = (fields, profile, familyChoice, matches) => {
    const alerts = [];
    if (!fields.exact_year) alerts.push('Choose the exact model year before treating any kit as vehicle-matched.');
    if (!fields.drivetrain || fields.drivetrain === 'unknown') alerts.push('2WD/RWD versus 4WD/AWD must be confirmed before direct add-to-cart is enabled.');
    if (fields.truck_load === 'unknown') alerts.push('Actual loaded axle or scale weight is needed before choosing capacity with confidence.');
    if (fields.rear_lift !== '0') alerts.push('Measure rear bracket spacing and use the manufacturer-approved spacer; advertised front lift is not spacer size.');
    if (fields.hitch === 'yes') alerts.push('Verify air-spring brackets against the installed 5th-wheel or gooseneck rails.');
    if (fields.factory_air === 'yes') alerts.push('Factory rear air or auto-leveling requires application-specific confirmation.');
    if (fields.special_trim === 'yes' || specialTerms.some(([, pattern]) => pattern.test(profile?.raw || ''))) alerts.push('Special trim/chassis selected: generic model-family fitment is not purchase approval.');
    if (familyChoice.adjusted) alerts.push(`${familyMeta[familyChoice.desired]?.label || familyChoice.desired} was preferred by use case, but another family has stronger vehicle evidence.`);
    if (!matches.length && profile) alerts.push('No current in-stock kit has enough year/model evidence. Search the manufacturer application guide by part number.');
    return alerts;
  };

  const renderResults = (fields) => {
    const vehicle = readVehicle();
    const profile = vehicleProfile(vehicle, fields);
    const vehicleMatches = getVehicleMatches(fields);
    const familyChoice = chooseFamily(fields, profile, vehicleMatches);
    const familyInfo = familyMeta[familyChoice.family] || familyMeta['loadlifter-5000'];
    const controlType = effectiveControl(fields);
    const controlInfo = controlMeta[controlType];
    const sorted = vehicleMatches.map((match) => {
      const preferenceIndex = familyChoice.preferences.indexOf(match.product.family);
      match.score += preferenceIndex >= 0 ? Math.max(0, 32 - preferenceIndex * 7) : 0;
      if (match.product.family === familyChoice.family) match.score += 35;
      if ((fields.frequency === 'daily' || fields.frequency === 'frequent') && match.product.family.includes('ultimate')) match.score += 10;
      return match;
    }).sort((a,b) => b.score-a.score || b.product.inventory-a.product.inventory || a.product.price-b.product.price);
    const shortlist = sorted.slice(0, 4);
    const controlChanged = controlType !== fields.control;
    const controlShortlist = controlProductsFor(controlType, fields);
    const bestSpring = shortlist[0]?.product || null;
    const bestControl = controlShortlist[0] || null;

    root.querySelector('[data-trm-air-result-title]').textContent = `${familyInfo.title} + ${controlInfo.title}`;
    root.querySelector('[data-trm-air-result-lead]').textContent = `${vehicle ? vehicle.label + ': ' : ''}${jobLabels[fields.job] || 'load support'}, ${loadLabels[fields.truck_load] || 'load not specified'}. ${educationOnly ? 'This is educational; choose a truck before treating a product as fit.' : 'Current catalog evidence is ranked below.'}`;
    root.querySelector('[data-trm-air-summary]').innerHTML = [
      vehicle?.label || 'Education-only mode', jobLabels[fields.job] || fields.job,
      fields.exact_year ? `${fields.exact_year} model year` : 'exact year needed',
      loadLabels[fields.truck_load] || fields.truck_load, fields.frequency,
      fields.rear_lift === '0' ? 'stock rear height' : `${fields.rear_lift} in rear lift`,
      controlChanged ? 'dual path recommended' : controlInfo.label
    ].map((item) => `<span>${escapeHtml(item)}</span>`).join('');

    root.querySelector('[data-trm-air-spring-rec]').innerHTML = recommendationHtml(familyInfo, 'Recommended spring family', familyWhy(fields, familyChoice.adjusted));
    root.querySelector('[data-trm-air-control-rec]').innerHTML = recommendationHtml({ ...controlInfo, capacity:controlInfo.copy }, controlChanged ? 'Control upgraded for uneven load' : 'Recommended air management', controlChanged ? 'Uneven load selected, so dual path is the safer recommendation.' : 'This matches the chosen adjustment style and load pattern.');

    const alertItems = buildAlerts(fields, profile, familyChoice, vehicleMatches);
    const alertBox = root.querySelector('[data-trm-air-fitment-alert]');
    alertBox.hidden = !alertItems.length;
    alertBox.innerHTML = alertItems.length ? `<b>Fitment checkpoints before purchase</b><ul>${alertItems.map((item) => `<li>${escapeHtml(item)}</li>`).join('')}</ul>` : '';

    root.querySelector('[data-trm-air-product-title]').textContent = educationOnly ? 'Choose a truck to unlock live fitment results' : shortlist.length ? `${shortlist.length} vehicle-ranked kits` : 'No purchase-ready indexed fit yet';
    root.querySelector('[data-trm-air-product-copy]').textContent = educationOnly ? 'The technical recommendation remains visible, but product cards stay locked without a vehicle.' : shortlist.length ? `Products with year/model evidence rank first. ${familyLabel(familyChoice.family)} is prioritized.` : 'A generic kit is not displayed as compatible when year/model evidence is missing.';
    root.querySelector('[data-trm-air-product-grid]').innerHTML = shortlist.length ? shortlist.map((match, rank) => renderProductCard(match, familyChoice.family, fields, rank)).join('') : `<div class="trm-air__no-results"><h3>Keep the truck. Verify the part.</h3><p>${educationOnly ? 'Choose your truck in My Truck Garage, then run the finder again.' : 'Ask Tex to search by year, drivetrain, axle, rear suspension and hitch hardware.'}</p></div>`;
    root.querySelector('[data-trm-air-control-grid]').innerHTML = controlResults(controlType, fields);
    const bundle = root.querySelector('[data-trm-air-bundle]');
    const springDirect = bestSpring && directCartAllowed(bestSpring, fields);
    const springHandle = bestSpring ? productHandle(bestSpring) : '';
    const controlHandle = bestControl ? productHandle(bestControl) : '';
    const bundleHandles = [springDirect ? springHandle : '', controlHandle].filter(Boolean);
    if (bundle) {
      const controlName = bestControl?.title || (controlType === 'manual' ? 'manual fill valves included with the spring kit' : controlInfo.title);
      const canBundle = springDirect && (controlType === 'manual' || Boolean(controlHandle));
      bundle.innerHTML = `<div><h3>${escapeHtml(bestSpring?.title || familyInfo.title)} + ${escapeHtml(controlName)}</h3><p>${canBundle ? 'Both sides of the build are ready for one cart action. The selected vehicle and fitment-review context travel with the line items.' : 'Review the spring-kit product page or ask Tex to clear the remaining chassis condition before the complete-system button is enabled.'}</p></div><button class="trm-air__cart-button" type="button" data-trm-air-add="${escapeHtml(bundleHandles.join(','))}" ${canBundle ? '' : 'disabled'}>${controlType === 'manual' ? 'Add spring kit' : 'Add complete matched system'}</button><p class="trm-air__bundle-status" data-trm-air-cart-status aria-live="polite"></p>`;
    }

    form.hidden = true;
    results.hidden = false;
    results.focus({ preventScroll:true });
    results.scrollIntoView({ behavior:'smooth', block:'start' });
  };

  const addCartItems = async (button) => {
    if (!button || button.disabled) return;
    const handles = String(button.dataset.trmAirAdd || '').split(',').filter(Boolean);
    const variants = handles.map((handle) => variantCatalog[handle]).filter(Boolean);
    if (!handles.length || variants.length !== handles.length) return;
    const fields = activeFields();
    const vehicle = readVehicle();
    const original = button.textContent;
    const status = root.querySelector('[data-trm-air-cart-status]');
    button.disabled = true;
    button.textContent = 'Adding…';
    try {
      const response = await fetch('/cart/add.js', {
        method:'POST',
        credentials:'same-origin',
        headers:{ 'Content-Type':'application/json', 'Accept':'application/json' },
        body:JSON.stringify({
          items:variants.map((variant) => ({
            id:Number(variant.variantId),
            quantity:1,
            properties:{
              'Selected vehicle': `${fields.exact_year || ''} ${vehicle?.label || ''}`.trim(),
              'Guide': 'TRM Air Spring System',
              'Fitment review': 'Required before fulfillment'
            }
          }))
        })
      });
      if (!response.ok) throw new Error('Cart request failed');
      button.textContent = handles.length > 1 ? 'Complete system added ✓' : 'Added ✓';
      if (status) status.textContent = handles.length > 1 ? 'Air spring kit and air management were added together.' : 'Item added. You can keep comparing or open the cart.';
      window.dispatchEvent(new CustomEvent('cart:refresh'));
      window.dispatchEvent(new CustomEvent('cart:updated'));
    } catch (error) {
      button.disabled = false;
      button.textContent = original;
      if (status) status.textContent = 'The cart did not update. Open the product page or try again.';
    }
  };

  const askTex = (event) => {
    if (event) event.preventDefault();
    window.dispatchEvent(new CustomEvent('trm:tex-open', { detail:{ source:'air-spring-desk-v21', finderKey:'AIR_SPRINGS' } }));
    const chat = document.querySelector('shopify-chat');
    if (chat && typeof chat.open === 'function') chat.open();
  };

  root.querySelectorAll('[data-trm-air-garage-open],[data-trm-air-stage-garage]').forEach((button) => button.addEventListener('click', (event) => openGarage(event.currentTarget)));
  root.querySelector('[data-trm-air-exact-year]')?.addEventListener('change', (event) => {
    if (event.currentTarget.value) saveVehicleDetail({ exactYear: event.currentTarget.value });
    syncVehicle();
  });
  root.querySelector('[name="drivetrain"]')?.addEventListener('change', (event) => {
    if (event.currentTarget.value) saveVehicleDetail({ drivetrain: event.currentTarget.value });
  });
  root.querySelectorAll('[data-trm-next]').forEach((button) => button.addEventListener('click', () => {
    if (currentStep === 1 && !readVehicle() && !educationOnly) { openGarage(button); return; }
    if (!validateStep(currentStep)) return;
    showStep(button.dataset.trmNext);
  }));
  root.querySelectorAll('[data-trm-back]').forEach((button) => button.addEventListener('click', () => showStep(button.dataset.trmBack)));
  progress.forEach((button) => button.addEventListener('click', () => {
    const step = Number(button.dataset.trmStepJump);
    if (step <= maxStep) showStep(step);
  }));
  root.querySelector('[data-trm-education]')?.addEventListener('click', () => { educationOnly = true; showStep(2); });
  root.querySelector('[data-trm-air-edit]')?.addEventListener('click', () => { results.hidden = true; form.hidden = false; showStep(2); });
  root.addEventListener('click', (event) => {
    const cartButton = event.target.closest('[data-trm-air-add]');
    if (cartButton) { addCartItems(cartButton); return; }
    if (event.target.closest('[data-trm-air-tex]')) askTex(event);
  });
  form?.addEventListener('submit', (event) => { event.preventDefault(); if (validateStep(5)) renderResults(activeFields()); });

  window.addEventListener('trm:vehicle-changed', () => { educationOnly = false; syncVehicle(); });
  window.addEventListener('trm:vehicle-detail-changed', syncVehicle);
  window.addEventListener('trm:vehicle-cleared', syncVehicle);
  window.addEventListener('storage', (event) => { if (event.key === GARAGE_KEY) syncVehicle(); });

  syncVehicle();
  showStep(1, false);
})();
